<template>
  <div v-if="loading" class="tl-page">
    <div class="tl-loading">
      <div class="tl-spinner"></div>
      <span>Loading tours...</span>
    </div>
  </div>

  <div v-else class="tl-page">
    <!-- ── Filter Bar ── -->
    <div class="tl-filters">
      <div class="tl-filters__selects">
        <select v-model="filters.tourName" class="tl-select">
          <option value="">Tour Name</option>
          <option v-for="name in filterOptions.tourNames" :key="name" :value="name">{{ name }}</option>
        </select>
        <select v-model="filters.tourCode" class="tl-select">
          <option value="">Tour Code</option>
          <option v-for="code in filterOptions.tourCodes" :key="code" :value="code">{{ code }}</option>
        </select>
        <select v-model="filters.cruiseLine" class="tl-select tl-select--wide">
          <option value="">Cruise Line</option>
          <option v-for="line in filterOptions.cruiseLines" :key="line" :value="line">{{ line }}</option>
        </select>
      </div>
      <div class="tl-filters__actions">
        <span class="tl-count">{{ filteredGroupedTours.length }} Groups</span>
        <button class="tl-btn tl-btn--ghost" @click="resetFilters">
          ↺ Reset
        </button>
      </div>
    </div>

    <div class="tl-header-bar mt-3">
      <h1 class="tl-title">Tour Groups</h1>

    </div>

    <div class="tl-table-card">
      <div class="tl-table-scroll">
        <table class="tl-table">
          <thead>
            <tr>
              <th class="tl-th">Tour Code</th>
              <th class="tl-th">Tour Name</th>
              <th class="tl-th">Cruise Line</th>
              <th class="tl-th">Years</th>
              <th class="tl-th">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="tourGroup in filteredGroupedTours" :key="tourGroup.key" class="tl-row"
              @click="openTourDetails(tourGroup)">
              <td class="tl-td">
                <span class="tl-pill tl-pill--blue">{{ tourGroup.tourCode }}</span>
              </td>
              <td class="tl-td tl-td--name">{{ tourGroup.tourName }}</td>
              <td class="tl-td">{{ tourGroup.cruiseLine }}</td>
              <td class="tl-td">
                <div class="tl-years">
                  <span v-for="year in tourGroup.years" :key="year" class="tl-pill tl-pill--green mr-1">
                    {{ year }}
                  </span>
                </div>
              </td>
              <td class="tl-td">
                <button class="tl-btn tl-btn--primary tl-btn--sm">
                  View Stats
                </button>
              </td>
            </tr>
            <tr v-if="!filteredGroupedTours.length">
              <td colspan="5" class="tl-empty">No tour groups found matching the filters.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ToursLondon',
  data() {
    return {
      tours: [],
      groupedTours: [],
      loading: true,
      filters: {
        tourName: '',
        tourCode: '',
        cruiseLine: ''
      }
    };
  },
  computed: {
    filterOptions() {
      const names = [...new Set(this.groupedTours.map(g => g.tourName))].sort();
      const codes = [...new Set(this.groupedTours.map(g => g.tourCode))].sort();
      const lines = [...new Set(this.groupedTours.map(g => g.cruiseLine))].sort();
      return { tourNames: names, tourCodes: codes, cruiseLines: lines };
    },
    filteredGroupedTours() {
      return this.groupedTours.filter(group => {
        const matchName = !this.filters.tourName || group.tourName === this.filters.tourName;
        const matchCode = !this.filters.tourCode || group.tourCode === this.filters.tourCode;
        const matchLine = !this.filters.cruiseLine || group.cruiseLine === this.filters.cruiseLine;
        return matchName && matchCode && matchLine;
      });
    }
  },
  mounted() {
    this.fetchTours();
  },
  methods: {
    async fetchTours() {
      this.loading = true;
      try {
        // Optimized query: only fetch necessary fields and no population
        const response = await this.$xhr.get('/api/tourlondons?fields[0]=tourCode&fields[1]=cruiseLine&fields[2]=tourName&fields[3]=year&pagination[limit]=1000');

        this.tours = response.data.data;
        this.groupTours();
      } catch (error) {
        console.error("Failed to fetch tours:", error);
      } finally {
        this.loading = false;
      }
    },
    groupTours() {
      const groups = {};
      this.tours.forEach((tour) => {
        const { tourCode, cruiseLine, tourName, year } = tour.attributes;
        const key = `${tourCode}-${cruiseLine}-${tourName}`;

        if (!groups[key]) {
          groups[key] = { key, tourCode, cruiseLine, tourName, years: [] };
        }
        if (year && !groups[key].years.includes(year)) {
          groups[key].years.push(year);
        }
      });

      // Sort years for each group
      Object.values(groups).forEach(group => {
        group.years.sort((a, b) => a - b);
      });

      this.groupedTours = Object.values(groups);
    },
    openTourDetails(tourGroup) {
      this.$router.push({ name: 'TourstatsLondon', params: { tourCode: tourGroup.tourCode } });
    },
    resetFilters() {
      this.filters.tourName = '';
      this.filters.tourCode = '';
      this.filters.cruiseLine = '';
    }
  }
};
</script>

<style scoped>
/* ────────────── Page & General ────────────── */
.tl-page {
  padding: 16px 20px;
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
  background: #f8fafc;
  min-height: 100vh;
}

.tl-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  padding: 12px 20px;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.tl-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #111827;
  margin: 0;
}

.mt-3 {
  margin-top: 1rem;
}

/* ── Filters ── */
.tl-filters {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .05);
  flex-wrap: wrap;
  gap: 16px;
}

.tl-filters__selects {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.tl-select {
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: .85rem;
  color: #374151;
  background: #f9fafb;
  outline: none;
  min-width: 140px;
}

.tl-select--wide {
  min-width: 200px;
}

.tl-filters__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tl-count {
  font-size: .82rem;
  font-weight: 700;
  color: #6b7280;
}

/* ────────────── Loading ────────────── */
.tl-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
  gap: 12px;
  color: #64748b;
}

.tl-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top-color: #E2923D;
  border-radius: 50%;
  animation: tl-spin .8s linear infinite;
}

@keyframes tl-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ────────────── Table Card ────────────── */
.tl-table-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .06);
}

.tl-table-scroll {
  overflow-x: auto;
  overflow-y: auto;
  /* Enable vertical scroll */
  max-height: 70vh;
  /* Set a max height for vertical scroll */
}

.tl-table {
  width: 100%;
  border-collapse: collapse;
  font-size: .85rem;
}

.tl-th {
  background: #E2923D;
  /* Match TourList header color */
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 14px 18px;
  text-align: center;
  color: #fff;
  /* White text for better contrast on orange */
  font-weight: 700;
  font-size: .68rem;
  text-transform: uppercase;
  letter-spacing: .05em;
  border: none;
}

.tl-td {
  padding: 12px 18px;
  border-bottom: 1px solid #f1f5f9;
  text-align: center;
  color: #334155;
  vertical-align: middle;
}

.tl-td--name {
  text-align: left;
  font-weight: 600;
  color: #1e293b;
}

.tl-row {
  cursor: pointer;
  transition: background .12s;
}

.tl-row:hover .tl-td {
  background: #fff8f1;
  /* Light orange hover effect */
}

.tl-empty {
  padding: 48px;
  text-align: center;
  color: #94a3b8;
}

/* ────────────── Components ────────────── */
.tl-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: .72rem;
  font-weight: 700;
}

.tl-pill--blue {
  background: #e0e7ff;
  color: #3730a3;
}

.tl-pill--green {
  background: #dcfce7;
  color: #166534;
}

.tl-btn {
  height: 32px;
  padding: 0 14px;
  font-size: .8rem;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all .15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.tl-btn--primary {
  background: #E2923D;
  color: #fff;
}

.tl-btn--ghost {
  background: #f1f5f9;
  color: #475569;
}

.tl-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.mr-1 {
  margin-right: 0.25rem;
}

.tl-years {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
}
</style>
